import React, { useEffect, useState } from "react";
import type { PositionData } from "@crypto-trading/common/browser";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import {
  exchangeDisplayName,
  formatProtectionPrice,
  protectionSummary,
  trailingSummary,
  type PositionSide,
  type ProtectionKind,
} from "@/lib/position-protection";
import {
  protectionErrorMessage,
  type PositionProtectionState,
} from "@/hooks/usePositionProtection";
import {
  PositionProtectionEditor,
  type ProtectionEditorPosition,
} from "./PositionProtectionEditor";

interface PositionProtectionPanelProps {
  position: PositionData;
  protection: PositionProtectionState;
  markPrice: number | null;
  liquidationPrice: number | null;
  variant: "mobile" | "desktop";
  /** Show the values without add/edit/remove (view-only drawers). */
  readOnly?: boolean;
}

type Notice = { tone: "success"; text: string } | null;

const toNumber = (value: string | number | null | undefined): number => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

const actionClass =
  "rounded-full border border-white/15 bg-[#171717] px-3 py-1 text-xs text-white hover:bg-[#262626] disabled:cursor-not-allowed disabled:opacity-50";

/**
 * TP and SL of an open position in Position Details: the live orders from
 * the exchange, with add, edit and remove. Shared by mobile and desktop.
 */
export const PositionProtectionPanel: React.FC<PositionProtectionPanelProps> = ({
  position,
  protection,
  markPrice,
  liquidationPrice,
  variant,
  readOnly = false,
}) => {
  const [editorKind, setEditorKind] = useState<ProtectionKind>("takeProfit");
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<ProtectionKind | null>(null);
  const [removing, setRemoving] = useState<ProtectionKind | null>(null);
  const [rowError, setRowError] = useState<{ kind: ProtectionKind; text: string } | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const side: PositionSide = position.positionType === "SHORT" ? "SHORT" : "LONG";
  const editorPosition: ProtectionEditorPosition = {
    symbol: position.symbol,
    side,
    entryPriceText: position.entryPrice,
    entryPrice: toNumber(position.entryPrice),
    leverage: toNumber(position.leverage) || 1,
    usdAmount: toNumber(position.usdAmount),
  };
  const exchange = exchangeDisplayName(position.exchangeName, position.exchangeCode);

  // Forget messages when another position is shown.
  useEffect(() => {
    setEditorOpen(false);
    setConfirmRemove(null);
    setRowError(null);
    setNotice(null);
    setWarnings([]);
  }, [position.positionId]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const afterSave = (warningsFromServer: string[], text: string) => {
    setWarnings(warningsFromServer);
    setNotice({ tone: "success", text });
  };

  const save = async (kind: ProtectionKind, price: number) => {
    const hadValue =
      kind === "takeProfit" ? protection.takeProfitPrice : protection.stopLossPrice;
    const response = await protection.save(
      kind === "takeProfit" ? { takeProfitPrice: price } : { stopLossPrice: price }
    );
    const label = kind === "takeProfit" ? "Take profit" : "Stop loss";
    afterSave(response.warnings ?? [], `${label} ${hadValue ? "updated" : "set"}`);
  };

  const remove = async (kind: ProtectionKind) => {
    setRowError(null);
    setNotice(null);
    setRemoving(kind);
    try {
      const response = await protection.save(
        kind === "takeProfit" ? { takeProfitPrice: null } : { stopLossPrice: null }
      );
      setConfirmRemove(null);
      afterSave(
        response.warnings ?? [],
        `${kind === "takeProfit" ? "Take profit" : "Stop loss"} removed`
      );
    } catch (error) {
      setRowError({ kind, text: protectionErrorMessage(error) });
    } finally {
      setRemoving(null);
    }
  };

  const openEditor = (kind: ProtectionKind) => {
    setRowError(null);
    setConfirmRemove(null);
    setEditorKind(kind);
    setEditorOpen(true);
  };

  const summaryArgs = {
    entryPrice: editorPosition.entryPrice,
    side,
    leverage: editorPosition.leverage,
  };
  const trailingOn = Boolean(protection.trailing) && !protection.takeProfitPrice;
  const busy = protection.isSaving;
  const canEdit = protection.editable && !readOnly;

  const renderRow = (kind: ProtectionKind) => {
    const isTp = kind === "takeProfit";
    const label = isTp ? "Take profit" : "Stop loss";
    const price = isTp ? protection.takeProfitPrice : protection.stopLossPrice;
    const orderCount = isTp
      ? protection.takeProfitOrderCount
      : protection.stopLossOrderCount;
    const dot = isTp ? "bg-green-400" : "bg-red-400";
    const valueTone = isTp ? "text-green-400" : "text-red-400";
    const showTrailing = isTp && trailingOn;

    if (confirmRemove === kind) {
      return (
        <div
          className="flex items-center justify-between gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-3"
          data-testid={`tpsl-row-${kind}`}
        >
          <p className="text-sm text-text-primary">Remove {label.toLowerCase()}?</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => remove(kind)}
              disabled={removing === kind}
              className="flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/20 px-3 py-1 text-xs font-semibold text-red-300 hover:bg-red-500/30 disabled:opacity-60"
            >
              {removing === kind && <Loader2 className="h-3 w-3 animate-spin" />}
              Remove
            </button>
            <button
              type="button"
              onClick={() => setConfirmRemove(null)}
              disabled={removing === kind}
              className={actionClass}
            >
              Keep
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className="rounded-xl border border-white/10 bg-[rgba(13,18,26,0.7)] p-3"
        data-testid={`tpsl-row-${kind}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-xs font-medium text-[#a3a3a3]">
              <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden />
              {label}
            </p>
            {showTrailing ? (
              <>
                <p className={`mt-1 text-sm font-semibold ${valueTone}`}>Trailing stop loss</p>
                <p className="text-xs text-text-secondary">
                  {trailingSummary(
                    protection.trailing?.activationPercent,
                    protection.trailing?.distancePercent,
                    side
                  ) ?? "Closes after a pullback from the best price"}
                </p>
              </>
            ) : price ? (
              <>
                <p
                  className={`mt-1 text-sm font-semibold tabular-nums ${valueTone} ${
                    protection.isLoading ? "opacity-60" : ""
                  }`}
                >
                  {formatProtectionPrice(price, position.entryPrice)}
                </p>
                <p className="text-xs text-text-secondary">
                  {protectionSummary(price, summaryArgs)}
                </p>
              </>
            ) : (
              <p className="mt-1 text-sm text-text-muted">
                {protection.isLoading ? "Checking…" : "None"}
              </p>
            )}
          </div>
          {canEdit && (
            <div className="flex shrink-0 items-center gap-2">
              {showTrailing ? (
                <button
                  type="button"
                  className={actionClass}
                  disabled={busy}
                  onClick={() => openEditor(kind)}
                >
                  Set fixed TP
                </button>
              ) : price ? (
                <>
                  <button
                    type="button"
                    className={actionClass}
                    disabled={busy}
                    onClick={() => openEditor(kind)}
                    aria-label={`Edit ${label.toLowerCase()}`}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={actionClass}
                    disabled={busy}
                    onClick={() => {
                      setRowError(null);
                      setConfirmRemove(kind);
                    }}
                    aria-label={`Remove ${label.toLowerCase()}`}
                  >
                    Remove
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className={actionClass}
                  disabled={busy || protection.isLoading}
                  onClick={() => openEditor(kind)}
                >
                  Add {label.toLowerCase()}
                </button>
              )}
            </div>
          )}
        </div>
        {orderCount > 1 && (
          <p className="mt-2 text-[11px] text-text-muted">
            {orderCount} {label.toLowerCase()} orders on the exchange; the
            nearest is shown.
          </p>
        )}
        {rowError?.kind === kind && (
          <p className="mt-2 text-xs text-red-400" role="alert">
            {rowError.text}
          </p>
        )}
      </div>
    );
  };

  const editingPrice =
    editorKind === "takeProfit"
      ? protection.takeProfitPrice
      : protection.stopLossPrice;

  return (
    <div className="space-y-2" data-testid="tpsl-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-text-primary">TP / SL</h3>
        {protection.isLoading && (
          <span className="flex items-center gap-1 text-[11px] text-text-muted">
            <Loader2 className="h-3 w-3 animate-spin" />
            Reading from {exchange}
          </span>
        )}
      </div>
      {renderRow("takeProfit")}
      {renderRow("stopLoss")}
      {protection.readFailed && (
        <p className="text-xs text-text-muted">
          Couldn't read TP/SL from {exchange}. Showing the last values set in
          Trigger.
        </p>
      )}
      {notice && (
        <p className="flex items-center gap-1.5 text-xs text-green-400" role="status">
          <Check className="h-3.5 w-3.5" aria-hidden />
          {notice.text}
        </p>
      )}
      {warnings.map((warning) => (
        <p
          key={warning}
          className="flex gap-1.5 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-2.5 py-1.5 text-xs text-yellow-300"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {warning}
        </p>
      ))}
      {canEdit && (
        <PositionProtectionEditor
          open={editorOpen}
          onOpenChange={setEditorOpen}
          kind={editorKind}
          position={editorPosition}
          markPrice={markPrice}
          liquidationPrice={liquidationPrice}
          currentPrice={editingPrice}
          turnsOffTrailing={editorKind === "takeProfit" && trailingOn}
          onSave={(price) => save(editorKind, price)}
          variant={variant}
        />
      )}
    </div>
  );
};
